import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  Dialog,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  DialogContent,
  Box,
  Grid,
  CircularProgress,
  AppBar,
  Toolbar,
  TextField,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useApi } from "../../utils/api";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";

const History = ({
  exerciseName,
  currentWorkoutDate,
  close,
  userId,
  embedded = false,
}) => {
  const { getExerciseHistory, getOneRepMax, getTonnage } = useApi();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState([
    moment().subtract(1, "months"),
    moment(),
  ]);
  const [chartMode, setChartMode] = useState("oneRepMax");
  const [oneRepMaxPoints, setOneRepMaxPoints] = useState([]);
  const [tonnagePoints, setTonnagePoints] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);

  const startUnix = useMemo(() => moment(dates[0]).unix(), [dates]);
  const endUnix = useMemo(() => moment(dates[1]).unix(), [dates]);

  useEffect(() => {
    const get = async () => {
      setLoading(true);
      const result = await getExerciseHistory(
        exerciseName,
        startUnix,
        endUnix,
        userId
      ).catch(() => []);
      setExercises(
        result
          .filter((e) =>
            currentWorkoutDate ? e.date !== currentWorkoutDate : true
          )
          .sort((e1, e2) => e2.date - e1.date)
      );
      setLoading(false);
    };
    get();
  }, [
    dates,
    exerciseName,
    startUnix,
    endUnix,
    userId,
    currentWorkoutDate,
    getExerciseHistory,
  ]);

  useEffect(() => {
    const loadCharts = async () => {
      setChartsLoading(true);
      try {
        const [oneRepData, tonnageData] = await Promise.all([
          getOneRepMax(exerciseName, startUnix, endUnix, userId),
          getTonnage(exerciseName, startUnix, endUnix, userId),
        ]);
        setOneRepMaxPoints(Array.isArray(oneRepData) ? oneRepData : []);
        setTonnagePoints(Array.isArray(tonnageData) ? tonnageData : []);
      } catch (e) {
        setOneRepMaxPoints([]);
        setTonnagePoints([]);
      } finally {
        setChartsLoading(false);
      }
    };
    loadCharts();
  }, [exerciseName, startUnix, endUnix, userId, getOneRepMax, getTonnage]);

  const has1RM = oneRepMaxPoints.length > 0;
  const hasTonnage = tonnagePoints.length > 0;

  const oneRepChartData = useMemo(
    () =>
      oneRepMaxPoints.map((p) => ({
        date: moment.unix(p.date).format("M/D"),
        value: p.estimated1RM,
      })),
    [oneRepMaxPoints]
  );

  const tonnageChartData = useMemo(
    () =>
      tonnagePoints.map((p) => ({
        date: moment.unix(p.date).format("M/D"),
        value: p.tonnage,
      })),
    [tonnagePoints]
  );

  const content = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexDirection: "column",
          mb: 1,
          gap: 2,
          pr: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "space-between",
            justifyContent: "space-between",
            width: "100%",
            pt: 1,
          }}
        >
          <Typography variant="h6">{exerciseName} History</Typography>
          <div></div>
          <IconButton
            color="inherit"
            onClick={close}
            aria-label="close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ width: "100%", p: 2 }}>
        {chartsLoading ? (
          <Grid display="flex" sx={{ m: 2 }} justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : has1RM || hasTonnage ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                mb: 2,
              }}
            >
              <DatePicker
                label="Start"
                value={dates[0]}
                onChange={(newValue) => {
                  if (!newValue) return;
                  const start = newValue;
                  const end =
                    dates[1] && moment(dates[1]).isAfter(start)
                      ? dates[1]
                      : start;
                  setDates([start, end]);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 160 },
                  },
                }}
              />
              <DatePicker
                label="End"
                value={dates[1]}
                onChange={(newValue) => {
                  if (!newValue) return;
                  const end = newValue;
                  const start =
                    dates[0] && moment(dates[0]).isBefore(end) ? dates[0] : end;
                  setDates([start, end]);
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { width: 160 },
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <ToggleButtonGroup
                value={chartMode}
                exclusive
                onChange={(_, v) => v && setChartMode(v)}
                size="small"
              >
                <ToggleButton value="oneRepMax">Est 1RM</ToggleButton>
                <ToggleButton value="tonnage">Tonnage</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box sx={{ mb: 2 }}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart
                  data={
                    chartMode === "oneRepMax"
                      ? oneRepChartData
                      : tonnageChartData
                  }
                  margin={{ top: 0, right: 20, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#ff0000"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        ) : (
          <Typography sx={{ mt: 2 }}>
            No 1RM or tonnage data for this exercise and date range.
          </Typography>
        )}
        {loading ? (
          <Grid display="flex" sx={{ m: 2 }} justifyContent="center">
            <CircularProgress />
          </Grid>
        ) : exercises.length === 0 ? (
          <Typography sx={{ mt: 2 }}>
            There's no history for this exercise and date range.
          </Typography>
        ) : (
          <>
            {exercises.map((exercise) => (
              <Box key={exercise.date} sx={{ mt: 1 }}>
                <Typography
                  sx={{ fontSize: 14, textAlign: "center" }}
                  color="text.secondary"
                  gutterBottom
                >
                  {moment.unix(exercise.date).format("dddd MMMM Do YYYY")}
                </Typography>
                <Table sx={{ mb: 4 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ padding: "5px" }}>#</TableCell>
                      <TableCell align="center">Reps</TableCell>
                      <TableCell>Weight</TableCell>
                      <TableCell>RPE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exercise.sets.map((set, setIndex) => (
                      <TableRow key={setIndex}>
                        <TableCell sx={{ padding: "5px" }}>
                          <Typography>{setIndex + 1}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography>{set.reps}</Typography>
                        </TableCell>
                        <TableCell>{set.weight}</TableCell>
                        <TableCell>{set.rpe}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ))}
          </>
        )}
      </Box>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowY: "auto",
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Dialog open fullScreen>
      <AppBar position="sticky">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={close}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6">{exerciseName} History</Typography>
            <Typography variant="caption">
              {moment(dates[0]).format("M.D.YYYY")} -{" "}
              {moment(dates[1]).format("M.D.YYYY")}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpenDatePicker(true)}
            aria-label="calendar"
          >
            <CalendarIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent display="flex" justifyContent="center">
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default History;
